import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { apiConfig } from '../config/api';
import { useAuth } from '../context/AuthContext';
import { clearMarketplaceOnboarding, setMarketplaceOnboarding } from '../services/marketplaceOnboardingService';

export default function MarketplaceSetupPage() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const hasResolvedRef = useRef(false);
  const [status, setStatus] = useState('loading');
  const [message, setMessage] = useState('Validating your AWS Marketplace registration...');
  const [offerType, setOfferType] = useState(null);
  const [alreadyRegistered, setAlreadyRegistered] = useState(false);

  const resolveCustomer = async () => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('marketplaceSessionId');
    const registrationToken = params.get('x-amzn-marketplace-token');
    const detectedOfferType = params.get('x-amzn-marketplace-offer-type') || null;
    if (detectedOfferType) setOfferType(detectedOfferType);

    try {
      let data;

      if (sessionId) {
        // Primary path: backend already called ResolveCustomer during handleSetup.
        // Fetch the pre-resolved session data — no raw token needed in the browser.
        const url = `${apiConfig.marketplaceSession}?sessionId=${encodeURIComponent(sessionId)}`;
        const response = await fetch(url, { credentials: 'include' });
        data = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(data.error || data.message || 'Marketplace session retrieval failed.');
        }

        // If the user is already authenticated, link inline so they go straight to dashboard.
        if (user && data.CustomerIdentifier && data.onboardingStatus === 'resolved') {
          const linkPayload = {
            sessionId,
            userId: user.email || user.username || user.attributes?.sub,
            cognitoSub: user.attributes?.sub || null,
            email: user.email || null,
          };
          const linkResponse = await fetch(apiConfig.marketplaceLink, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(linkPayload),
          });
          if (linkResponse.ok) {
            data = { ...data, onboardingStatus: 'registered' };
          }
        }
      } else if (registrationToken) {
        // Fallback path: backend did not resolve eagerly (token passed in URL).
        const payload = { registrationToken, offerType: detectedOfferType };

        if (user) {
          payload.userId = user.email || user.username || user.attributes?.sub;
          payload.cognitoSub = user.attributes?.sub || null;
          payload.email = user.email || null;
        }

        const response = await fetch(apiConfig.marketplaceResolve, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });

        data = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(data.error || data.message || 'Marketplace validation failed.');
        }
      } else {
        throw new Error('No marketplace session ID or registration token found. Please restart from AWS Marketplace.');
      }

      if (data.offerType) setOfferType(data.offerType);

      if (data.onboardingStatus === 'registered') {
        // Linked inline during resolve — no further action needed
        clearMarketplaceOnboarding();
        setStatus('success');
        setMessage('Your AWS Marketplace account has been linked. Redirecting to your dashboard...');
        setTimeout(() => navigate('/dashboard', { replace: true }), 2000);
      } else if (data.onboardingStatus === 'already_registered') {
        clearMarketplaceOnboarding();
        setAlreadyRegistered(true);
        setStatus('success');
        setMessage(data.message || 'Your AWS account is already registered. Please sign in.');
      } else {
        // 'resolved' — store for later linking after registration/sign-in
        if (data.CustomerIdentifier || sessionId) {
          setMarketplaceOnboarding({
            sessionId: sessionId || null,
            customerIdentifier: data.CustomerIdentifier || null,
            customerAWSAccountId: data.CustomerAWSAccountId || null,
            productCode: data.ProductCode || null,
            licenseArn: data.LicenseArn || null,
            offerType: data.offerType || detectedOfferType,
          });
        }
        setStatus('success');
        setMessage('AWS Marketplace validation completed. Continue to account creation.');
      }
    } catch (error) {
      setStatus('error');
      setMessage(error.message || 'Marketplace validation failed.');
    }
  };

  useEffect(() => {
    if (authLoading) {
      return; // wait for auth to resolve before calling the marketplace endpoint
    }

    if (hasResolvedRef.current) {
      return;
    }

    hasResolvedRef.current = true;
    resolveCustomer();
  }, [authLoading]);

  const isFreeTrial = offerType === 'free-trial';

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh', px: 2 }}>
      <Card sx={{ maxWidth: 520, width: '100%', p: 2 }}>
        <CardContent>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="h5">Set up your account</Typography>
            {isFreeTrial && (
              <Chip label="Free Trial" color="success" size="small" />
            )}
          </Stack>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            {isFreeTrial
              ? 'Complete the AWS Marketplace free trial handoff before continuing.'
              : 'Complete the AWS Marketplace handoff before continuing to registration.'}
          </Typography>

          {status === 'loading' && (
            <Stack spacing={2} alignItems="center" sx={{ py: 2 }}>
              <CircularProgress />
              <Typography color="text.secondary">{message}</Typography>
            </Stack>
          )}

          {status === 'success' && (
            <Stack spacing={3}>
              <Alert severity={alreadyRegistered ? 'info' : 'success'}>{message}</Alert>
              {alreadyRegistered ? (
                <Button component={RouterLink} to="/login?fromMarketplace=1&alreadyRegistered=1" variant="contained" fullWidth>
                  Sign in to your account
                </Button>
              ) : (
                <>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <Button component={RouterLink} to="/register?fromMarketplace=1" variant="contained" fullWidth>
                      Create account
                    </Button>
                    <Button component={RouterLink} to="/login?fromMarketplace=1" variant="outlined" fullWidth>
                      Sign in to existing account
                    </Button>
                  </Stack>
                  <Button
                    variant="text"
                    onClick={() => {
                      navigate('/register?fromMarketplace=1', { replace: true });
                    }}
                  >
                    Continue now
                  </Button>
                </>
              )}
            </Stack>
          )}

          {status === 'error' && (
            <Stack spacing={3}>
              <Alert severity="error">{message}</Alert>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Button
                  variant="contained"
                  onClick={() => {
                    setStatus('loading');
                    setMessage('Retrying AWS Marketplace validation...');
                    resolveCustomer();
                  }}
                  fullWidth
                >
                  Retry
                </Button>
                <Button component={RouterLink} to="/" variant="outlined" fullWidth>
                  Back to home
                </Button>
              </Stack>
            </Stack>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}